import styled from 'styled-components';

export const StyledBlogPostPage = styled.div`
  width: 100%;

  .blog-post-page {
    width: 100%;
    max-width: 900px;
    margin: 60px auto;
    padding: 24px;

    &__back {
      margin-bottom: 24px;
    }

    &__cover-image {
      width: 100%;
      margin-bottom: 40px;
      border-radius: 12px;
      overflow: hidden;
      display: flex;
      justify-content: center;

      img {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 500px;
        object-fit: cover;
      }
    }

    &__header {
      margin-bottom: 40px;

      &__category {
        margin-bottom: 16px;
      }

      &__title {
        margin-bottom: 16px;
      }

      &__excerpt {
        margin-bottom: 24px;
        opacity: 0.8;
      }

      &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        &__item {
          width: fit-content;
        }
      }
    }

    &__content {
      line-height: 1.8;

      h2 {
        margin: 32px 0 16px 0;
        font-size: 28px;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.mineShaft};
      }

      h3 {
        margin: 40px 0 16px 0;
        font-size: 22px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.mineShaft};
      }

      h4 {
        margin: 28px 0 8px 0;
        font-size: 18px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.mineShaft};
      }

      .blog-subheading {
        margin: 32px 0 8px 0;
        font-size: 18px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.mineShaft};
      }

      p {
        margin: 16px 0;
        font-size: 16px;
        line-height: 1.8;
        color: ${({ theme }) => theme.colors.raven};
      }

      ul,
      ol {
        margin: 16px 0;
        padding-left: 24px;
        color: ${({ theme }) => theme.colors.raven};

        li {
          margin: 8px 0;
          font-size: 16px;
          line-height: 1.8;
        }
      }

      strong {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.mineShaft};
      }

      em {
        font-style: italic;
      }

      a {
        color: ${({ theme }) => theme.colors.mainOrange};
        text-decoration: none;
        transition: opacity 0.3s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 24px 0;
      }

      blockquote {
        border-left: 4px solid ${({ theme }) => theme.colors.mainOrange};
        padding-left: 20px;
        margin: 24px 0;
        font-style: italic;
        opacity: 0.9;
      }

      code {
        background-color: ${({ theme }) => theme.colors.athensGray};
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Courier New', monospace;
        font-size: 14px;
      }

      pre {
        background-color: ${({ theme }) => theme.colors.athensGray};
        padding: 16px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 24px 0;

        code {
          background: none;
          padding: 0;
        }
      }

      /* ── Reusable blog content blocks ─────────────────────────── */

      .blog-divider {
        border: none;
        height: 2px;
        background: linear-gradient(
          90deg,
          transparent 0%,
          ${({ theme }) => theme.colors.mercury} 20%,
          ${({ theme }) => theme.colors.mainOrange} 50%,
          ${({ theme }) => theme.colors.mercury} 80%,
          transparent 100%
        );
        margin: 40px 0;
      }

      .blog-callout {
        display: flex;
        gap: 16px;
        padding: 20px 24px;
        border-radius: 12px;
        margin: 28px 0;
        border-left: 4px solid ${({ theme }) => theme.colors.mainBlue};
        background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainBlue, 0.05)};

        &--info {
          border-left-color: ${({ theme }) => theme.colors.mainBlue};
          background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainBlue, 0.05)};
        }

        &--tip {
          border-left-color: ${({ theme }) => theme.colors.lima};
          background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.lima, 0.05)};
        }

        &--warning {
          border-left-color: ${({ theme }) => theme.colors.mainOrange};
          background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainOrange, 0.05)};
        }

        &--danger {
          border-left-color: ${({ theme }) => theme.colors.flamingo};
          background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.flamingo, 0.05)};
        }

        .blog-callout__icon {
          font-size: 24px;
          flex-shrink: 0;
          line-height: 1.4;
        }

        .blog-callout__body {
          flex: 1;
          min-width: 0;

          > strong {
            display: block;
            margin-bottom: 4px;
            font-size: 15px;
          }

          p {
            margin: 0 0 8px;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }

      .blog-key-stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 28px 20px;
        border-radius: 14px;
        background: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.mercury};
        box-shadow: 0 2px 12px ${({ theme }) => theme.colors.getTransparentColor(theme.colors.black, 0.04)};

        .blog-key-stat__value {
          font-size: 2.2rem;
          font-weight: 800;
          color: ${({ theme }) => theme.colors.mainOrange};
          line-height: 1.1;
          margin-bottom: 8px;
          white-space: nowrap;
        }

        .blog-key-stat__label {
          font-size: 14px;
          color: ${({ theme }) => theme.colors.raven};
          line-height: 1.4;
        }
      }

      .blog-stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 16px;
        margin: 28px 0;
      }

      .blog-section-image {
        margin: 32px 0;
        text-align: center;

        img {
          width: 100%;
          max-width: 100%;
          border-radius: 12px;
          box-shadow: 0 4px 20px ${({ theme }) => theme.colors.getTransparentColor(theme.colors.black, 0.08)};
        }

        .blog-section-image__caption {
          margin-top: 12px;
          font-size: 13px;
          color: ${({ theme }) => theme.colors.raven};
          font-style: italic;
        }
      }

      .blog-highlight-box {
        position: relative;
        padding: 28px;
        margin: 32px 0;
        border-radius: 14px;
        background: linear-gradient(
          135deg,
          ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainOrange, 0.04)} 0%,
          ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainBlue, 0.04)} 100%
        );
        border: 1px solid ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainOrange, 0.2)};

        .blog-highlight-box__title {
          font-size: 18px;
          font-weight: 700;
          color: ${({ theme }) => theme.colors.mainOrange};
          margin-bottom: 12px;
        }

        p {
          margin: 8px 0;
        }

        ul,
        ol {
          margin: 8px 0;
          padding-left: 20px;
        }
      }

      .blog-timeline {
        position: relative;
        margin: 32px 0;
        padding-left: 40px;

        &::before {
          content: '';
          position: absolute;
          left: 9px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(
            180deg,
            ${({ theme }) => theme.colors.mainOrange} 0%,
            ${({ theme }) => theme.colors.mainBlue} 100%
          );
          border-radius: 2px;
        }

        .blog-timeline__item {
          position: relative;
          padding: 16px 0 24px;

          &::before {
            content: '';
            position: absolute;
            left: -40px;
            top: 18px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: ${({ theme }) => theme.colors.mainOrange};
            border: 3px solid ${({ theme }) => theme.colors.white};
            box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.mainOrange};
          }

          &:last-child {
            padding-bottom: 0;
          }
        }

        .blog-timeline__time {
          font-size: 14px;
          font-weight: 700;
          color: ${({ theme }) => theme.colors.mainOrange};
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .blog-timeline__title {
          font-size: 17px;
          font-weight: 600;
          color: ${({ theme }) => theme.colors.mineShaft};
          margin-bottom: 4px;
        }

        .blog-timeline__desc {
          font-size: 15px;
          color: ${({ theme }) => theme.colors.raven};
          line-height: 1.6;
          margin: 0;
        }
      }

      .blog-comparison-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 28px 0;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid ${({ theme }) => theme.colors.mercury};

        thead {
          th {
            padding: 14px 18px;
            font-size: 14px;
            font-weight: 700;
            color: ${({ theme }) => theme.colors.white};
            background: ${({ theme }) => theme.colors.mainBlue};
            text-align: left;
            border-bottom: 2px solid ${({ theme }) => theme.colors.mainBlue};
          }
        }

        tbody {
          tr {
            transition: background 0.15s;

            &:nth-child(even) {
              background: ${({ theme }) => theme.colors.athensGray};
            }

            &:hover {
              background: ${({ theme }) => theme.colors.getTransparentColor(theme.colors.mainOrange, 0.05)};
            }
          }

          td {
            padding: 12px 18px;
            font-size: 15px;
            color: ${({ theme }) => theme.colors.raven};
            border-bottom: 1px solid ${({ theme }) => theme.colors.mercury};
            vertical-align: top;

            strong {
              color: ${({ theme }) => theme.colors.mineShaft};
            }
          }
        }
      }

      .blog-quote {
        position: relative;
        padding: 28px 32px 28px 48px;
        margin: 32px 0;
        background: ${({ theme }) => theme.colors.athensGray};
        border-radius: 12px;
        border-left: none;
        font-style: normal;

        &::before {
          content: '\\201C';
          position: absolute;
          left: 14px;
          top: 8px;
          font-size: 56px;
          line-height: 1;
          color: ${({ theme }) => theme.colors.mainOrange};
          opacity: 0.4;
          font-family: Georgia, serif;
        }

        p {
          margin: 0;
          font-size: 17px;
          font-style: italic;
          color: ${({ theme }) => theme.colors.mineShaft};
          line-height: 1.7;
        }

        .blog-quote__source {
          margin-top: 12px;
          font-size: 13px;
          font-style: normal;
          color: ${({ theme }) => theme.colors.raven};
        }
      }

      .blog-list-cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        margin: 28px 0;
        list-style: none;
        padding: 0;
      }

      .blog-list-card {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 18px 22px;
        background: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.mercury};
        border-radius: 12px;
        transition: box-shadow 0.2s;

        &:hover {
          box-shadow: 0 2px 12px ${({ theme }) => theme.colors.getTransparentColor(theme.colors.black, 0.06)};
        }

        .blog-list-card__icon {
          font-size: 24px;
          flex-shrink: 0;
          line-height: 1;
          margin-top: 2px;
        }

        .blog-list-card__number {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 50%;
          background: ${({ theme }) => theme.colors.mainOrange};
          color: ${({ theme }) => theme.colors.white};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
        }

        .blog-list-card__body {
          flex: 1;
          min-width: 0;

          strong {
            display: block;
            font-size: 16px;
            margin-bottom: 4px;
            color: ${({ theme }) => theme.colors.mineShaft};
          }

          p {
            margin: 0;
            font-size: 15px;
          }
        }
      }

      .blog-two-col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin: 28px 0;
      }
    }
  }

  @media (max-width: 768px) {
    .blog-post-page {
      padding: 16px;
      margin: 40px auto;

      &__cover-image {
        margin-bottom: 24px;

        img {
          max-height: 300px;
        }
      }

      &__header {
        margin-bottom: 24px;

        &__title {
          font-size: 28px;
        }
      }

      &__content {
        h2 {
          font-size: 24px;
        }

        h3 {
          font-size: 20px;
        }

        p,
        li {
          font-size: 15px;
        }

        .blog-stats-grid {
          grid-template-columns: 1fr 1fr;
        }

        .blog-key-stat .blog-key-stat__value {
          font-size: 1.6rem;
        }

        .blog-timeline {
          padding-left: 36px;
        }

        .blog-comparison-table {
          display: block;
          overflow-x: auto;
        }

        .blog-callout {
          padding: 16px 18px;
        }

        .blog-highlight-box {
          padding: 20px;
        }

        .blog-quote {
          padding: 20px 20px 20px 40px;
        }

        .blog-two-col {
          grid-template-columns: 1fr;
        }
      }
    }
  }
`;
